import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getAllStudents } from '../utils/studentCache';
import { supabase } from '../lib/supabase';
import ImportButton from '../components/ui/ImportButton';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { Search, Languages, Save, Eye, Edit2, Trash2, X } from 'lucide-react';
import SearchableSelect from '../components/ui/SearchableSelect';
import { saveStyledXlsx } from '../lib/excelStyle';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';

// PostgREST returns max 1000 rows per request — page through everything
async function fetchAllPaged(buildQuery) {
  let all = [], from = 0;
  while (true) {
    const { data, error } = await buildQuery().range(from, from + 999);
    if (error) throw error;
    all = all.concat(data || []);
    if (!data || data.length < 1000) break;
    from += 1000;
  }
  return all;
}
// Local (IST) date — toISOString() gives the UTC date, i.e. "yesterday" before 5:30am IST
const localDate = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const activeStudent = st => st.is_active !== false && !['Discontinued', 'Shifted'].includes(st.student_status);
const chunk = (arr, n = 100) => { const out = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; };

const TRAINERS = [
  'Aravind', 'Anoop Chandran', 'Piyush', 'Nithin Raj',
  'Adarsh', 'Vishnu', 'Ashmi', 'Anumon', 'Imthisam'
];


  function getBatchDiscipline(batchName) {
    if (!batchName) return null;
    const b = batchName.toUpperCase();
    if (b.startsWith('EM') || b.startsWith('TM')) return 'mechanical';
    if (b.startsWith('EC') || b.startsWith('TC')) return 'civil';
    return null;
  }
function sortBatches(batches) {
  // Ongoing first, then Upcoming, then Completed; inside each group the newest batch (latest start date) comes first.
  const rank = s => { const v = String(s || '').trim().toLowerCase(); return (v === 'ongoing' || v === 'active' || v === 'running') ? 0 : (v === 'completed' || v === 'closed') ? 2 : 1; };
  return [...(batches || [])].sort((a, b) => {
    const d = rank(a.status) - rank(b.status);
    if (d) return d;
    const sa = a.start_date || '', sb = b.start_date || '';
    if (sa !== sb) return sa < sb ? 1 : -1;
    return (b.batch_name || '').localeCompare(a.batch_name || '', undefined, { numeric: true });
  });
}

// Sort students by register number the natural way (TM23002 before TM23010).
const byRegNo = (a, b) => String(a.register_number || '').localeCompare(String(b.register_number || ''), undefined, { numeric: true });

const OWNER_MAIL = 'ershadarmstech@gmail.com';
const OFFICE_MAILS = ['armstechac@gmail.com', 'armstechekm@gmail.com'];

export default function LanguageTraining() {
  const { canEdit, canEditBatch, assignedBatches, isCoordinator, isAdmin, user } = useAuth();
  // staff assigned to specific batches can change only those batches; every other batch is view-only
  const meMail = String(user?.email || '').trim().toLowerCase();
  const batchRestricted = meMail !== OWNER_MAIL && !OFFICE_MAILS.includes(meMail) && !isAdmin && ((assignedBatches || []).length > 0 || !!isCoordinator);

  const [sessions, setSessions]   = useState([]);
  const [students, setStudents]   = useState([]);
  const [batches, setBatches]     = useState([]);
  const ownBatch = name => !batchRestricted || (typeof canEditBatch === 'function' && batches.some(b => b.batch_name === name && canEditBatch(b.id)));
  const canEditLT = canEdit('language_training') || batchRestricted;   // an assigned batch grants edit on that batch even if the section is View
  const canEditRow = name => canEditLT && ownBatch(name);
  const [topics, setTopics]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState('mark');
  const [saving, setSaving]       = useState(false);
  const [summarySelected, setSummarySelected] = useState(new Set());
  const [historySelected, setHistorySelected] = useState(new Set());

  // ── Mark Session controls ──
  const [language, setLanguage]   = useState('english');
  const [date, setDate]           = useState(() => localDate());
  const [topic, setTopic]         = useState('');
  const [trainer, setTrainer]     = useState('');
  const [attendance, setAttendance] = useState({});
  const [hasExisting, setHasExisting] = useState(false);   // attendance already recorded for this date + language
  const attSeq = useRef(0);

  // ── Search filters (two modes) ──
  const [searchMode, setSearchMode]   = useState('batch');   // 'batch' | 'student'
  const [selectedBatch, setSelectedBatch] = useState('');
  const [studentSearch, setStudentSearch] = useState('');

  // ── Summary/History filters ──
  const [historySearch, setHistorySearch]   = useState('');
  const [historyBatch, setHistoryBatch]     = useState('');
  const [historyTrainer, setHistoryTrainer] = useState('');
  const [historyTopic, setHistoryTopic]     = useState('');
  const [viewSession, setViewSession]       = useState(null);
  const [editSession, setEditSession]       = useState(null);
  const [editForm, setEditForm]             = useState({});
  const [editSaving, setEditSaving]         = useState(false);

  function openEditSession(s) {
    if (!canEditRow(s.students?.batch_name)) { toast.error('You can change only your own batches'); return; }
    setEditSession(s);
    setEditForm({ session_date: s.session_date||'', language: s.language||'english', attended: s.attended??true, session_topic: s.session_topic||'', trainer_name: s.trainer_name||'', remarks: s.remarks||'' });
  }

  async function saveEditSession() {
    if (!canEditRow(editSession?.students?.batch_name)) { toast.error('You can change only your own batches'); return; }
    if (!editForm.session_date) { toast.error('Date is required'); return; }
    setEditSaving(true);
    const { error } = await supabase.from('language_training').update({
      ...editForm, session_topic: editForm.session_topic || null, trainer_name: editForm.trainer_name || null, remarks: editForm.remarks || null,
    }).eq('id', editSession.id);
    if (error) toast.error(error.message);
    else { toast.success('Session updated ✅'); setEditSession(null); loadSessions(); }
    setEditSaving(false);
  }

  useEffect(() => { loadAll(); }, []);

  // keep the existing attendance in sync with whatever is on screen (date, language, batch / student, student list)
  useEffect(() => { loadSessionAttendance(visibleStudents()); }, [date, language, searchMode, selectedBatch, studentSearch, students]); // eslint-disable-line

  // Real-time updates (debounced)
  useEffect(() => {
    let _rt = null;
    const _reload = () => { clearTimeout(_rt); _rt = setTimeout(() => { loadSessions(); }, 800); };
    const ch = supabase.channel('languagetraining-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'language_training' }, () => _reload())
      .subscribe();
    return () => { clearTimeout(_rt); supabase.removeChannel(ch); };
  }, []);

  async function loadSessions() {
    try {
      const rows = await fetchAllPaged(() => supabase.from('language_training')
        .select('*, students(full_name, register_number, batch_name)')
        .order('session_date', { ascending: false }).order('id'));
      setSessions(rows);
    } catch (e) { toast.error('Could not load sessions: ' + e.message); }
  }
  function loadData() { return loadSessions(); }

  async function loadAll() {
    const [bat, top] = await Promise.all([
      supabase.from('batches').select('id, batch_name, batch_code, status, start_date').order('batch_name'),
      supabase.from('topics').select('id, topic_name, short_code, discipline').order('topic_name'),
    ]);
    setBatches(sortBatches(bat.data || []));
    setTopics(top.data || []);
    try { setStudents(await getAllStudents()); } catch (e) { toast.error('Could not load students: ' + e.message); }
    await loadSessions();
    setLoading(false);
  }

  async function loadSessionAttendance(list) {
    const seq = ++attSeq.current;
    const sIds = (list || []).map(s => s.id);
    if (sIds.length === 0) { setAttendance({}); setHasExisting(false); return; }
    let rows = [];
    for (const ids of chunk(sIds)) {
      const { data } = await supabase.from('language_training').select('student_id, attended')
        .eq('session_date', date).eq('language', language).in('student_id', ids);
      rows = rows.concat(data || []);
    }
    if (seq !== attSeq.current) return;      // a newer request has started — ignore this one
    const map = {};
    rows.forEach(r => { map[r.student_id] = r.attended; });
    setAttendance(map);
    setHasExisting(rows.length > 0);
  }

  // ── Which students to show based on search mode ──
  function visibleStudents() {
    const pool = students.filter(activeStudent);     // discontinued / shifted students are not part of a session
    if (searchMode === 'batch') {
      if (!selectedBatch) return [];
      return pool.filter(s => s.batch_name === selectedBatch).sort((a,b)=>(a.register_number||'').localeCompare(b.register_number||''));
    }
    if (!studentSearch) return [];
    return pool.filter(s => s.id === studentSearch);  // studentSearch holds the picked student's id
  }

  const filteredStudents = visibleStudents();
  const markAllowed = canEditLT && filteredStudents.every(s => ownBatch(s.batch_name));

  async function saveSession() {
    if (filteredStudents.some(s => !canEditRow(s.batch_name))) { toast.error('You can mark sessions only for your own batches'); return; }
    if (!topic) { toast.error('Please select a Topic before saving'); return; }
    if (!trainer) { toast.error('Please select a Trainer before saving'); return; }
    if (filteredStudents.length === 0) { toast.error('No students to save'); return; }
    setSaving(true);
    try {
      const studentIds = filteredStudents.map(s => s.id);
      // 1. read what is already there (keep its remarks)
      let existing = [];
      for (const ids of chunk(studentIds)) {
        const { data, error } = await supabase.from('language_training').select('id, student_id, remarks')
          .eq('session_date', date).eq('language', language).in('student_id', ids);
        if (error) throw error;
        existing = existing.concat(data || []);
      }
      const remarksBy = {}; existing.forEach(r => { remarksBy[r.student_id] = r.remarks; });
      // 2. insert the fresh records first — if this fails nothing has been lost
      const records = filteredStudents.map(s => ({
        student_id: s.id, language, session_date: date,
        attended: attendance[s.id] || false,
        session_topic: topic || null,
        trainer_name: trainer || null,
        remarks: remarksBy[s.id] || null,
      }));
      for (const part of chunk(records, 200)) {
        const { error } = await supabase.from('language_training').insert(part);
        if (error) throw error;
      }
      // 3. only now remove the old rows
      for (const ids of chunk(existing.map(r => r.id))) {
        const { error } = await supabase.from('language_training').delete().in('id', ids);
        if (error) toast.error('Saved, but old records could not be cleaned up: ' + error.message);
      }
      toast.success(`${language} session saved for ${date} — ${records.length} students`);
      await loadSessions();
      loadSessionAttendance(filteredStudents);
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setSaving(false);
  }

  function markAll(val) {
    const map = { ...attendance };
    filteredStudents.forEach(s => { map[s.id] = val; });
    setAttendance(map);
  }

  // ── Summary per student (computed only while the Summary tab is open) ──
  const summary = useMemo(() => {
    if (activeTab !== 'summary') return [];
    const by = {};
    sessions.forEach(x => {
      const e = by[x.student_id] || (by[x.student_id] = { eng: 0, totalEng: 0, hin: 0, totalHin: 0, last: null });
      if (x.language === 'english') { e.totalEng++; if (x.attended) e.eng++; }
      else if (x.language === 'hindi') { e.totalHin++; if (x.attended) e.hin++; }
      if (!e.last || String(x.session_date || '') > String(e.last.session_date || '')) e.last = x;
    });
    const q = historySearch.toLowerCase();
    return students.filter(activeStudent).filter(s =>
      (!historyBatch || s.batch_name === historyBatch || s.batch_id === historyBatch) &&
      (!q || s.full_name?.toLowerCase().includes(q) || s.register_number?.toLowerCase().includes(q))
    ).map(s => {
      const e = by[s.id] || { eng: 0, totalEng: 0, hin: 0, totalHin: 0, last: null };
      return { ...s, eng: e.eng, hin: e.hin, totalEng: e.totalEng, totalHin: e.totalHin, lastTopic: e.last?.session_topic || '—', lastTrainer: e.last?.trainer_name || '—' };
    }).sort((a, b) => (a.register_number || '').localeCompare(b.register_number || ''));
  }, [activeTab, sessions, students, historyBatch, historySearch]);

  const pct = (p, t) => t > 0 ? Math.round(p / t * 100) : null;
  function exportSummary() {
    if (!summary.length) { toast.error('Nothing to export'); return; }
    saveStyledXlsx(`Language_Summary_${localDate()}.xlsx`, [{ name: 'Summary', title: 'Language Training — Student Summary', rows: summary.map((s, i) => ({
      'S.No': i + 1, 'Reg No': s.register_number || '', Name: s.full_name || '', Batch: s.batch_name || '',
      'Last Topic': s.lastTopic === '—' ? '' : s.lastTopic, 'Last Trainer': s.lastTrainer === '—' ? '' : s.lastTrainer,
      'English Present': s.eng, 'English Total': s.totalEng, 'English %': pct(s.eng, s.totalEng) ?? '',
      'Hindi Present': s.hin, 'Hindi Total': s.totalHin, 'Hindi %': pct(s.hin, s.totalHin) ?? '',
    })) }]);
  }
  function exportHistory() {
    if (!filteredHistory.length) { toast.error('Nothing to export'); return; }
    saveStyledXlsx(`Language_Sessions_${localDate()}.xlsx`, [{ name: 'Sessions', title: 'Language Training — Session History', rows: filteredHistory.map((s, i) => ({
      'S.No': i + 1, Date: s.session_date || '', Language: s.language || '', Student: s.students?.full_name || '', 'Reg No': s.students?.register_number || '',
      Batch: s.students?.batch_name || '', Attended: s.attended ? 'Present' : 'Absent', Topic: s.session_topic || '', Trainer: s.trainer_name || '', Remarks: s.remarks || '',
    })) }]);
  }

  // ── Filtered history ──
  const uniqueTrainers = [...new Set(sessions.map(s => s.trainer_name).filter(Boolean))].sort();
  const uniqueTopics   = [...new Set(sessions.map(s => s.session_topic).filter(Boolean))].sort();

  const filteredHistory = sessions.filter(s => {
    const matchBatch   = !historyBatch   || s.students?.batch_name === historyBatch;
    const matchName    = !historySearch  || s.students?.full_name?.toLowerCase().includes(historySearch.toLowerCase()) || s.students?.register_number?.toLowerCase().includes(historySearch.toLowerCase());
    const matchTrainer = !historyTrainer || s.trainer_name === historyTrainer;
    const matchTopic   = !historyTopic   || s.session_topic === historyTopic;
    return matchBatch && matchName && matchTrainer && matchTopic;
  });

  return (
    <div className="page">
      <div className="page-header">
        <div><h1 className="page-title">Language Training</h1><p className="page-subtitle">Track English and Hindi session attendance</p></div>
        {canEditLT && !batchRestricted && <ImportButton configKey="language_sessions" onImported={() => loadSessions()} />}
      </div>

      <div className="tabs">
        <div className={`tab ${activeTab === 'mark' ? 'active' : ''}`} onClick={() => setActiveTab('mark')}>Mark Session</div>
        <div className={`tab ${activeTab === 'summary' ? 'active' : ''}`} onClick={() => setActiveTab('summary')}>Student Summary</div>
        <div className={`tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>Session History</div>
      </div>

      {/* ── MARK SESSION TAB ── */}
      {activeTab === 'mark' && (
        <>
          {/* Search mode toggle */}
          <div className="card" style={{ marginBottom: 16, padding: '14px 20px' }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, color: 'var(--text2)' }}>Search Students</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>

              {/* Mode toggle */}
              <div className="form-group">
                <label>Search By</label>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className={`btn btn-sm ${searchMode === 'batch' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setSearchMode('batch'); setStudentSearch(''); }}>By Batch</button>
                  <button className={`btn btn-sm ${searchMode === 'student' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => { setSearchMode('student'); setSelectedBatch(''); }}>By Student</button>
                </div>
              </div>

              {/* Batch dropdown */}
              {searchMode === 'batch' && (
                <div className="form-group" style={{ minWidth: 240 }}>
                  <label>Select Batch</label>
                  <SearchableSelect
                    options={batches.map(b=>({value:b.batch_name,label:b.batch_name}))}
                    value={selectedBatch}
                    onChange={v => { setSelectedBatch(v); setAttendance({}); }}
                    placeholder="— Select a Batch —"
                    searchPlaceholder="Search batch..."
                    style={{ width: 240 }}
                  />
                </div>
              )}

              {/* Student name search */}
              {searchMode === 'student' && (
                <div className="form-group" style={{ minWidth: 260 }}>
                  <label>Student Name / Register No.</label>
                  <SearchableSelect
                    options={[...students].sort(byRegNo).map(s => ({ value: s.id, label: `${s.full_name} (${s.register_number})` }))}
                    value={studentSearch}
                    onChange={v => { setStudentSearch(v); setAttendance({}); }}
                    placeholder="Search student..."
                    searchPlaceholder="Search student..."
                    style={{ width: 260 }}
                  />
                </div>
              )}

              {filteredStudents.length > 0 && (
                <div style={{ alignSelf: 'flex-end', fontSize: 12, color: 'var(--text3)', paddingBottom: 8 }}>
                  {filteredStudents.length} student{filteredStudents.length !== 1 ? 's' : ''} found
                </div>
              )}
            </div>
          </div>

          {/* Session settings */}
          <div className="card" style={{ marginBottom: 16, padding: '16px 20px' }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, color: 'var(--text2)' }}>Session Settings</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              {/* Language */}
              <div className="form-group" style={{ minWidth: 200 }}>
                <label>Language</label>
                <div style={{ display: 'flex', gap: 4 }}>
                  {['english', 'hindi', 'other'].map(l => (
                    <button key={l} className={`btn btn-sm ${language === l ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setLanguage(l)} style={{ textTransform: 'capitalize' }}>{l}</button>
                  ))}
                </div>
              </div>

              {/* Date */}
              <div className="form-group">
                <label>Session Date</label>
                <DateInput value={date} onChange={e => setDate(e.target.value)} style={{ width: 160 }} />
              </div>

              {/* Topic dropdown from topics table */}
              <div className="form-group" style={{ minWidth: 240 }}>
                <label>Session Topic</label>
                <SearchableSelect
                  options={(()=>{const disc=getBatchDiscipline(selectedBatch);return (disc?topics.filter(t=>!t.discipline||t.discipline.toLowerCase()===disc):topics);})().map(t=>({value:t.topic_name,label:t.short_code?`${t.short_code} — ${t.topic_name}`:t.topic_name}))}
                  value={topic}
                  onChange={v=>setTopic(v)}
                  placeholder="Select Topic..."
                  searchPlaceholder="Search topic..."
                />

              </div>

              {/* Trainer dropdown */}
              <div className="form-group" style={{ minWidth: 180 }}>
                <label>Trainer</label>
                <select value={trainer} onChange={e => setTrainer(e.target.value)} style={{ width: 180 }}>
                  <option value="">Select Trainer</option>
                  {TRAINERS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* No students state */}
          {filteredStudents.length === 0 && (
            <div className="empty-state">
              <Languages />
              <h3>{searchMode === 'batch' ? 'Select a batch to mark attendance' : 'Search a student name or register number'}</h3>
              <p>Use the search options above to find students</p>
            </div>
          )}

          {/* Student attendance table */}
          {filteredStudents.length > 0 && (
            <>
              {hasExisting && <div style={{ marginBottom: 10, padding: '8px 12px', background: 'var(--amber-light)', border: '1px solid var(--amber)', borderRadius: 8, fontSize: 12 }}>
                ℹ️ Attendance for <strong>{language}</strong> on <strong>{date}</strong> is already recorded — the ticks below show it. Saving will update it.
              </div>}
              {canEditLT && !markAllowed && <div style={{ marginBottom: 10, padding: '8px 12px', background: 'var(--amber-light)', border: '1px solid var(--amber)', borderRadius: 8, fontSize: 12 }}>👁 View only - you can mark sessions only for the batches assigned to you.</div>}
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                {markAllowed && <button className="btn btn-secondary btn-sm" onClick={() => markAll(true)}>✓ Mark All Present</button>}
                {markAllowed && <button className="btn btn-secondary btn-sm" onClick={() => markAll(false)}>✗ Mark All Absent</button>}
                <span style={{ fontSize: 12, color: 'var(--green)', marginLeft: 4 }}>
                  {filteredStudents.filter(s => attendance[s.id]).length} present
                </span>
                <span style={{ fontSize: 12, color: 'var(--red)' }}>
                  {filteredStudents.filter(s => !attendance[s.id]).length} absent
                </span>
              </div>

              <div className="table-wrap" style={{ marginBottom: 16 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Reg No.</th>
                      <th>Name</th>
                      <th>Batch</th>
                      <th style={{ textAlign: 'center' }}>Attendance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map(s => (
                      <tr key={s.id} style={{ background: attendance[s.id] ? 'rgba(34,197,94,0.04)' : undefined }}>
                        <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--accent)' }}>{s.register_number}</td>
                        <td style={{ fontWeight: 500 }}>{s.full_name}</td>
                        <td style={{ fontSize: 12, color: 'var(--text3)' }}>{s.batch_name || '—'}</td>
                        <td style={{ textAlign: 'center' }}>
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: markAllowed ? 'pointer' : 'default' }}>
                            <input
                              type="checkbox"
                              checked={attendance[s.id] || false}
                              onChange={e => markAllowed && setAttendance(prev => ({ ...prev, [s.id]: e.target.checked }))}
                              disabled={!markAllowed}
                            />
                            <span style={{ fontSize: 12, fontWeight: 500, color: attendance[s.id] ? 'var(--green)' : 'var(--text3)' }}>
                              {attendance[s.id] ? 'Present' : 'Absent'}
                            </span>
                          </label>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {markAllowed && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, alignItems: 'center' }}>
                  {topic && <span style={{ fontSize: 12, color: 'var(--text3)' }}>Topic: <strong>{topic}</strong></span>}
                  {trainer && <span style={{ fontSize: 12, color: 'var(--text3)' }}>Trainer: <strong>{trainer}</strong></span>}
                  <button className="btn btn-primary" onClick={saveSession} disabled={saving}>
                    <Save size={14} />
                    {saving ? 'Saving...' : `Save ${language} Session — ${date}`}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── SUMMARY TAB ── */}
      {activeTab === 'summary' && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems:'center' }}>
            <SearchableSelect
              options={batches.map(b => ({ value: b.batch_name, label: b.batch_name }))}
              value={historyBatch}
              onChange={v => setHistoryBatch(v)}
              placeholder="All Batches"
              searchPlaceholder="Search batch..."
              style={{ width: 220 }}
            />
            <div className="search-bar" style={{ width: 240 }}>
              <Search />
              <input placeholder="Search student..." value={historySearch} onChange={e => setHistorySearch(e.target.value)} />
            </div>
            <button className="btn btn-ghost btn-sm" onClick={exportSummary}>⬇ Export</button>
            {summarySelected.size>0&&canEditLT&&students.filter(x=>summarySelected.has(x.id)).every(x=>ownBatch(x.batch_name))&&(
              <span style={{fontSize:12,color:'var(--accent)',fontWeight:600}}>
                {summarySelected.size} selected
                <button className="btn btn-danger btn-sm" style={{marginLeft:8,fontSize:11}}
                  onClick={async()=>{
                    if(!window.confirm(`Delete ALL language training records for ${summarySelected.size} students? This cannot be undone.`)) return;
                    let failed = null;
                    for (const ids of chunk([...summarySelected])) {
                      const { error } = await supabase.from('language_training').delete().in('student_id', ids);
                      if (error) failed = error;
                    }
                    if (failed) toast.error(failed.message); else toast.success('Records deleted');
                    setSummarySelected(new Set());
                    loadSessions();
                  }}>🗑 Delete Selected</button>
              </span>
            )}
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{width:36}}><input type="checkbox" checked={summary.length>0&&summary.every(s=>summarySelected.has(s.id))} onChange={e=>setSummarySelected(e.target.checked?new Set(summary.map(s=>s.id)):new Set())}/></th>
                  <th>Reg No.</th><th>Name</th><th>Batch</th>
                  <th>Last Topic</th><th>Last Trainer</th>
                  <th>Eng Present</th><th>Eng Total</th><th>Eng %</th>
                  <th>Hin Present</th><th>Hin Total</th><th>Hin %</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {summary.length === 0
                  ? <tr><td colSpan={13}><div className="empty-state"><p>No data found</p></div></td></tr>
                  : summary.map(s => (
                    <tr key={s.id}>
                      <td><input type="checkbox" checked={summarySelected.has(s.id)} onChange={()=>setSummarySelected(prev=>{const n=new Set(prev);n.has(s.id)?n.delete(s.id):n.add(s.id);return n;})}/></td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--accent)' }}>{s.register_number}</td>
                      <td style={{ fontWeight: 500 }}>{s.full_name}</td>
                      <td style={{ fontSize: 12, color: 'var(--text3)' }}>{s.batch_name || '—'}</td>
                      <td style={{ fontSize: 11 }}>{s.lastTopic}</td>
                      <td style={{ fontSize: 11 }}>{s.lastTrainer}</td>
                      <td>{s.eng}</td>
                      <td style={{ color: 'var(--text3)' }}>{s.totalEng}</td>
                      <td>
                        <span style={{ color: s.totalEng > 0 && (s.eng / s.totalEng * 100) >= 75 ? 'var(--green)' : 'var(--amber)', fontWeight: 600 }}>
                          {s.totalEng > 0 ? pct(s.eng, s.totalEng) + '%' : '—'}
                        </span>
                      </td>
                      <td>{s.hin}</td>
                      <td style={{ color: 'var(--text3)' }}>{s.totalHin}</td>
                      <td>
                        <span style={{ color: s.totalHin > 0 && (s.hin / s.totalHin * 100) >= 75 ? 'var(--green)' : 'var(--amber)', fontWeight: 600 }}>
                          {s.totalHin > 0 ? pct(s.hin, s.totalHin) + '%' : '—'}
                        </span>
                      </td>
                      <td>
                        {canEditRow(s.batch_name) && <button className="btn btn-ghost btn-sm" style={{color:'var(--red)',fontSize:11}}
                          onClick={async()=>{
                            if(!canEditRow(s.batch_name)) { toast.error('You can change only your own batches'); return; }
                            if(!window.confirm(`Delete ALL language training records for ${s.full_name}? This cannot be undone.`)) return;
                            const { error } = await supabase.from('language_training').delete().eq('student_id', s.id);
                            if (error) { toast.error(error.message); return; }
                            toast.success('Records deleted');
                            loadSessions();
                          }}>🗑 Delete</button>}
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── HISTORY TAB ── */}
      {activeTab === 'history' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <SearchableSelect options={batches.map(b => ({ value: b.batch_name, label: b.batch_name }))} value={historyBatch} onChange={v => setHistoryBatch(v)} placeholder="All Batches" searchPlaceholder="Search batch..." style={{ minWidth: 130, maxWidth: 150 }} />
            <div className="search-bar"><Search /><input placeholder="Search student..." value={historySearch} onChange={e => setHistorySearch(e.target.value)} /></div>
            <select style={{ width: 130 }} value={historyTrainer} onChange={e => setHistoryTrainer(e.target.value)}>
              <option value="">All Trainers</option>
              {uniqueTrainers.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select style={{ width: 130 }} value={historyTopic} onChange={e => setHistoryTopic(e.target.value)}>
              <option value="">All Topics</option>
              {uniqueTopics.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {(historyBatch || historySearch || historyTrainer || historyTopic) && (
              <button className="btn btn-ghost btn-sm" onClick={() => { setHistoryBatch(''); setHistorySearch(''); setHistoryTrainer(''); setHistoryTopic(''); }}>✕ Clear</button>
            )}
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>{filteredHistory.length} records</span>
            <button className="btn btn-ghost btn-sm" onClick={exportHistory}>⬇ Export</button>
            {historySelected.size > 0 && canEditLT && sessions.filter(x => historySelected.has(x.id)).every(x => ownBatch(x.students?.batch_name)) && (
              <span style={{fontSize:12,color:'var(--accent)',fontWeight:600}}>
                {historySelected.size} selected
                <button className="btn btn-sm btn-danger" style={{marginLeft:8,fontSize:11}}
                  onClick={async()=>{
                    if(!window.confirm(`Delete ${historySelected.size} records?`)) return;
                    let failed = null;
                    for (const ids of chunk([...historySelected])) {
                      const { error } = await supabase.from('language_training').delete().in('id', ids);
                      if (error) failed = error;
                    }
                    setHistorySelected(new Set());
                    loadSessions();
                    if (failed) toast.error(failed.message); else toast.success('Deleted');
                  }}>🗑 Delete Selected</button>
              </span>
            )}
          </div>
          {loading ? <div className="loading"><div className="spinner" /></div> : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{width:36}}>
                      <input type="checkbox"
                        checked={historySelected.size===filteredHistory.length&&filteredHistory.length>0}
                        onChange={e=>setHistorySelected(e.target.checked?new Set(filteredHistory.map(s=>s.id)):new Set())}
                      />
                    </th>
                    <th>Date</th><th>Language</th><th>Student</th><th>Batch</th><th>Attended</th><th>Topic</th><th>Trainer</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.length === 0
                    ? <tr><td colSpan={9}><div className="empty-state"><Languages /><h3>No sessions recorded</h3></div></td></tr>
                    : filteredHistory.map(s => (
                      <tr key={s.id}>
                        <td>
                          <input type="checkbox"
                            checked={historySelected.has(s.id)}
                            onChange={()=>setHistorySelected(prev=>{const n=new Set(prev);n.has(s.id)?n.delete(s.id):n.add(s.id);return n;})}
                          />
                        </td>
                        <td style={{ fontSize: 12 }}>{fmtDMY(s.session_date)}</td>
                        <td><span className={`badge ${s.language === 'english' ? 'badge-blue' : s.language === 'hindi' ? 'badge-amber' : 'badge-gray'}`}>{s.language}</span></td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{s.students?.full_name}</div>
                          <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text3)' }}>{s.students?.register_number}</div>
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text3)' }}>{s.students?.batch_name || '—'}</td>
                        <td><span className={`badge ${s.attended ? 'badge-green' : 'badge-red'}`}>{s.attended ? 'Present' : 'Absent'}</span></td>
                        <td style={{ fontSize: 12 }}>{s.session_topic || '—'}</td>
                        <td style={{ fontSize: 12 }}>{s.trainer_name || '—'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-ghost btn-sm" title="View" onClick={() => setViewSession(s)}><Eye size={13} /></button>
                            {canEditRow(s.students?.batch_name) && <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => openEditSession(s)}><Edit2 size={13} /></button>}
                            {canEditRow(s.students?.batch_name) && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} title="Delete"
                              onClick={async () => {
                                if (!canEditRow(s.students?.batch_name)) return;
                                if (!window.confirm('Delete this session record?')) return;
                                const { error } = await supabase.from('language_training').delete().eq('id', s.id);
                                if (error) toast.error(error.message);
                                else { toast.success('Deleted'); loadData(); }
                              }}
                            ><Trash2 size={13} /></button>}
                          </div>
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* VIEW SESSION MODAL */}
      {viewSession && (
        <div className="modal-overlay" onClick={() => setViewSession(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Session Details</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setViewSession(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {[
                ['Student', viewSession.students?.full_name],
                ['Register No.', viewSession.students?.register_number],
                ['Batch', viewSession.students?.batch_name],
                ['Date', viewSession.session_date],
                ['Language', viewSession.language],
                ['Attended', viewSession.attended ? '✅ Present' : '❌ Absent'],
                ['Topic', viewSession.session_topic || '—'],
                ['Trainer', viewSession.trainer_name || '—'],
                ['Remarks', viewSession.remarks || '—'],
              ].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text3)' }}>{l}</span>
                  <span style={{ fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>
            <div className="modal-footer"><button className="btn btn-primary" onClick={() => setViewSession(null)}>Close</button></div>
          </div>
        </div>
      )}

      {/* EDIT SESSION MODAL */}
      {editSession && (
        <div className="modal-overlay" onClick={() => setEditSession(null)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Edit Session — {editSession.students?.full_name}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditSession(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label>Date</label><DateInput value={editForm.session_date} onChange={e => setEditForm(f => ({ ...f, session_date: e.target.value }))} /></div>
                <div className="form-group"><label>Language</label>
                  <select value={editForm.language} onChange={e => setEditForm(f => ({ ...f, language: e.target.value }))}>
                    <option value="english">English</option>
                    <option value="hindi">Hindi</option>
                    <option value="other">Other</option>
                    <option value="arabic">Arabic</option>
                  </select>
                </div>
                <div className="form-group"><label>Attended</label>
                  <select value={editForm.attended ? 'yes' : 'no'} onChange={e => setEditForm(f => ({ ...f, attended: e.target.value === 'yes' }))}>
                    <option value="yes">Present</option>
                    <option value="no">Absent</option>
                  </select>
                </div>
                <div className="form-group"><label>Topic</label>
                        <SearchableSelect
                          options={(()=>{const disc=getBatchDiscipline(editSession.students?.batch_name);return (disc?topics.filter(t=>!t.discipline||t.discipline.toLowerCase()===disc):topics);})().map(t=>({value:t.topic_name,label:t.short_code?`${t.short_code} — ${t.topic_name}`:t.topic_name}))}
                          value={editForm.session_topic||''}
                          onChange={v=>setEditForm(f=>({...f,session_topic:v}))}
                          placeholder="Select topic..."
                          searchPlaceholder="Search topic..."
                        />
                      </div>
                <div className="form-group"><label>Trainer</label>
                  <select value={editForm.trainer_name||''} onChange={e => setEditForm(f => ({ ...f, trainer_name: e.target.value }))}>
                    <option value="">Select Trainer</option>
                    {[...new Set([...TRAINERS, editForm.trainer_name].filter(Boolean))].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group full"><label>Remarks</label><input value={editForm.remarks} onChange={e => setEditForm(f => ({ ...f, remarks: e.target.value }))} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditSession(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEditSession} disabled={editSaving}>{editSaving ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
